import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import axios from 'axios';
import {
  BanknotesIcon,
  ArrowPathIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';

export default function AdminStripeAccount() {
  const { user } = useAuth();
  const { permissions } = usePermissions();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('balance');
  const [selectedOrganisme, setSelectedOrganisme] = useState('SAR');

  const isSuperAdmin = user?.role === 'super_admin';
  const hasFinanceAccess = isSuperAdmin || permissions?.finances?.access;

  const organismes = [
    { code: 'SAR', name: 'Syndicat Apicole de la Réunion', color: 'bg-green-500' },
    { code: 'AMAIR', name: 'Association de la Maison de l\'Apiculture de l\'Île de la Réunion', color: 'bg-blue-500' },
    { code: 'ADAR', name: 'Association pour le Développement de l\'Apiculture à la Réunion', color: 'bg-purple-500' }
  ];

  useEffect(() => {
    if (!user || !['admin', 'super_admin'].includes(user.role)) {
      navigate('/');
      return;
    }
    
    if (!hasFinanceAccess) {
      navigate('/admin/dashboard');
      return;
    }
    
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, hasFinanceAccess]);

  useEffect(() => {
    if (hasFinanceAccess) {
      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganisme, hasFinanceAccess]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [balanceRes, transactionsRes, paymentsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/stripe-account/balance?organisme=${selectedOrganisme}`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/stripe-account/transactions?limit=20&organisme=${selectedOrganisme}`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/stripe-account/payments?limit=20&organisme=${selectedOrganisme}`, { headers }),
      ]);

      setBalance(balanceRes.data);
      setTransactions(transactionsRes.data.transactions || []);
      setPayments(paymentsRes.data.payments || []);
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTransactionTypeLabel = (type) => {
    const labels = {
      charge: 'Paiement',
      payment: 'Paiement',
      payout: 'Virement',
      refund: 'Remboursement',
      adjustment: 'Ajustement',
      application_fee: 'Frais',
      stripe_fee: 'Frais Stripe',
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const styles = {
      succeeded: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      failed: 'bg-red-100 text-red-700',
      available: 'bg-green-100 text-green-700',
    };
    const labels = {
      succeeded: 'Réussi',
      pending: 'En attente',
      failed: 'Échoué',
      available: 'Disponible',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-slate-100 text-slate-700'}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-honey-500"></div>
      </div>
    );
  }

  if (!hasFinanceAccess) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-slate-600">Vous n'avez pas accès à cette page</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Compte Stripe</h1>
              <p className="text-slate-600 mt-1">
                Visualisation du solde et des transactions Stripe
              </p>
              {balance && !balance.livemode && (
                <span className="inline-flex items-center mt-2 px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                  <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                  Mode Test
                </span>
              )}
            </div>
            <button
              onClick={fetchData}
              className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ArrowPathIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Sélecteur d'organisme */}
        <div className="mb-6">
          <div className="bg-white rounded-xl shadow-soft p-4">
            <h3 className="text-sm font-medium text-slate-700 mb-3">Sélectionner un organisme</h3>
            <div className="flex flex-wrap gap-3">
              {organismes.map((org) => (
                <button
                  key={org.code}
                  onClick={() => setSelectedOrganisme(org.code)}
                  className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedOrganisme === org.code
                      ? 'bg-honey-100 text-honey-800 border-2 border-honey-300'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border-2 border-transparent'
                  }`}
                >
                  <div className={`w-3 h-3 rounded-full ${org.color} mr-2`}></div>
                  <div className="text-left">
                    <div className="font-semibold">{org.code}</div>
                    <div className="text-xs opacity-75">{org.name}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Solde Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Solde disponible */}
          <div className="bg-white rounded-xl shadow-soft p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-lg mr-4">
                  <BanknotesIcon className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Solde disponible - {selectedOrganisme}</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {balance?.available?.[0]?.amount || '0.00'} €
                  </p>
                </div>
              </div>
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
            </div>
            <p className="text-xs text-slate-400">
              Montant disponible pour les virements
            </p>
          </div>

          {/* Solde en attente */}
          <div className="bg-white rounded-xl shadow-soft p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="p-3 bg-yellow-100 rounded-lg mr-4">
                  <ClockIcon className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Solde en attente - {selectedOrganisme}</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {balance?.pending?.[0]?.amount || '0.00'} €
                  </p>
                </div>
              </div>
              <ClockIcon className="h-8 w-8 text-yellow-500" />
            </div>
            <p className="text-xs text-slate-400">
              Paiements en cours de traitement
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-soft">
          <div className="border-b border-slate-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('transactions')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'transactions'
                    ? 'border-honey-500 text-honey-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                Transactions récentes
              </button>
              <button
                onClick={() => setActiveTab('payments')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'payments'
                    ? 'border-honey-500 text-honey-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                Paiements récents
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'transactions' && (
              <div className="overflow-x-auto">
                {transactions.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    Aucune transaction récente
                  </div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-slate-500 border-b border-slate-100">
                        <th className="pb-3 font-medium">Date</th>
                        <th className="pb-3 font-medium">Type</th>
                        <th className="pb-3 font-medium">Description</th>
                        <th className="pb-3 font-medium text-right">Montant</th>
                        <th className="pb-3 font-medium text-right">Frais</th>
                        <th className="pb-3 font-medium text-right">Net</th>
                        <th className="pb-3 font-medium">Statut</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {transactions.map((t) => (
                        <tr key={t.id} className="text-sm">
                          <td className="py-3 text-slate-600">{formatDate(t.created)}</td>
                          <td className="py-3">
                            <div className="flex items-center">
                              {parseFloat(t.amount) >= 0 ? (
                                <ArrowTrendingUpIcon className="h-4 w-4 text-green-500 mr-2" />
                              ) : (
                                <ArrowTrendingDownIcon className="h-4 w-4 text-red-500 mr-2" />
                              )}
                              {getTransactionTypeLabel(t.type)}
                            </div>
                          </td>
                          <td className="py-3 text-slate-600 max-w-xs truncate">
                            {t.description || '-'}
                          </td>
                          <td className={`py-3 text-right font-medium ${parseFloat(t.amount) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {parseFloat(t.amount) >= 0 ? '+' : ''}{t.amount} €
                          </td>
                          <td className="py-3 text-right text-slate-500">-{t.fee} €</td>
                          <td className="py-3 text-right font-medium text-slate-900">{t.net} €</td>
                          <td className="py-3">{getStatusBadge(t.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {activeTab === 'payments' && (
              <div className="overflow-x-auto">
                {payments.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    Aucun paiement récent
                  </div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-slate-500 border-b border-slate-100">
                        <th className="pb-3 font-medium">Date</th>
                        <th className="pb-3 font-medium">Email client</th>
                        <th className="pb-3 font-medium">Description</th>
                        <th className="pb-3 font-medium text-right">Montant</th>
                        <th className="pb-3 font-medium">Statut</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {payments.map((p) => (
                        <tr key={p.id} className="text-sm">
                          <td className="py-3 text-slate-600">{formatDate(p.created)}</td>
                          <td className="py-3 text-slate-900">{p.customerEmail || '-'}</td>
                          <td className="py-3 text-slate-600 max-w-xs truncate">
                            {p.description || '-'}
                          </td>
                          <td className="py-3 text-right font-medium text-slate-900">
                            {p.amount} €
                          </td>
                          <td className="py-3">{getStatusBadge(p.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="mt-6 p-4 bg-slate-100 rounded-lg">
          <div className="flex items-start">
            <CreditCardIcon className="h-5 w-5 text-slate-500 mr-3 mt-0.5" />
            <div className="text-sm text-slate-600">
              <p className="font-medium text-slate-700 mb-1">Informations</p>
              <p>
                Les données affichées sont récupérées directement depuis votre compte Stripe.
                Pour effectuer des virements ou gérer votre compte, connectez-vous au{' '}
                <a
                  href="https://dashboard.stripe.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-honey-600 hover:text-honey-700 underline"
                >
                  Dashboard Stripe
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
